import { useMemo } from 'react';
import {
  Dropdown,
  Field,
  Input,
  Option,
  Switch,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import {
  FilterOperator,
  FilterType,
  type FilterKind,
  type IFilterableColumn,
  type IFilterOption,
} from './filter.types';
import { DEFAULT_TOGGLE_LABELS } from './filter.const';

interface FilterValueInputProps {
  column: IFilterableColumn;
  operator: FilterOperator;
  value: string | string[];
  onChange: (value: string | string[]) => void;
}

const isMultiOperator = (op: FilterOperator): boolean =>
  op === FilterOperator.In || op === FilterOperator.NIn;

/**
 * Default `kind` for callers that haven't set one (legacy columns built
 * by hand without going through `metadataFilter`). Derives a sensible UI
 * shape from the backend `type` + presence of options/toggleOptions.
 */
const inferKind = (column: IFilterableColumn): FilterKind => {
  if (column.toggleOptions) return 'toggle';
  if (column.options?.length) return 'select';
  switch (column.type) {
    case FilterType.Number:
      return 'number';
    case FilterType.Date:
      return 'date';
    default:
      return 'text';
  }
};

/**
 * The actually-rendered kind. The only mutation against the column-declared
 * `kind` is escalation: a `select` becomes `multi-select` when the operator
 * is In/NIn, and a `number`/`text` becomes `multi-select` (chip-like) the
 * same way. Single source of truth for the renderer is this function.
 */
const resolveKind = (
  declared: FilterKind,
  operator: FilterOperator
): FilterKind => {
  if (declared === 'toggle') return 'toggle';
  if (isMultiOperator(operator)) return 'multi-select';
  return declared;
};

// ---- Input Renderers ----

const SwitchInput = ({
  value,
  toggleOptions,
  onChange,
}: {
  value: string;
  toggleOptions: [IFilterOption, IFilterOption];
  onChange: (v: string) => void;
}) => {
  const [onOption, offOption] = toggleOptions;
  const isOn = value === onOption.value;
  return (
    <Switch
      label={
        isOn
          ? onOption.label ?? DEFAULT_TOGGLE_LABELS.on
          : offOption.label ?? DEFAULT_TOGGLE_LABELS.off
      }
      checked={isOn}
      onChange={(_, data) =>
        onChange(data.checked ? onOption.value : offOption.value)
      }
    />
  );
};

const MultiSelectInput = ({
  value,
  options,
  onChange,
}: {
  value: string[];
  options: IFilterOption[];
  onChange: (v: string[]) => void;
}) => {
  // No options: use text input, user enters comma-separated values
  if (options.length === 0) {
    return (
      <Input
        value={value.join(',')}
        onChange={(_, data) =>
          onChange(data.value ? data.value.split(',').map((v) => v.trim()) : [])
        }
        placeholder="Enter comma-separated values"
      />
    );
  }

  return (
    <Dropdown
      multiselect
      placeholder="Select values"
      selectedOptions={value}
      value={value
        .map((v) => options.find((o) => o.value === v)?.label ?? v)
        .join(', ')}
      onOptionSelect={(_, data) => onChange(data.selectedOptions)}
    >
      {options.map((opt) => (
        <Option key={opt.value} value={opt.value}>
          {opt.label ?? opt.value}
        </Option>
      ))}
    </Dropdown>
  );
};

const SingleSelectInput = ({
  value,
  options,
  onChange,
}: {
  value: string;
  options: IFilterOption[];
  onChange: (v: string) => void;
}) => (
  <Dropdown
    placeholder="Select value"
    value={options.find((o) => o.value === value)?.label ?? value}
    selectedOptions={value ? [value] : []}
    onOptionSelect={(_, data) => onChange(data.optionValue ?? '')}
  >
    {options.map((opt) => (
      <Option key={opt.value} value={opt.value}>
        {opt.label ?? opt.value}
      </Option>
    ))}
  </Dropdown>
);

const TextInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => <Input value={value} onChange={(_, data) => onChange(data.value)} />;

const NumberInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <Input
    type="number"
    value={value}
    onChange={(_, data) => onChange(data.value)}
  />
);

const DateInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) => (
  <DatePicker
    value={value ? new Date(value) : null}
    onSelectDate={(date) => onChange(date?.toISOString() ?? '')}
    placeholder="Select date"
  />
);

// ---- Main Component ----

export const FilterValueInput = ({
  column,
  operator,
  value,
  onChange,
}: FilterValueInputProps) => {
  const options = column.options ?? [];
  const declaredKind = column.kind ?? inferKind(column);
  const kind = useMemo(
    () => resolveKind(declaredKind, operator),
    [declaredKind, operator]
  );

  const renderInput = () => {
    switch (kind) {
      case 'toggle': {
        const stringValue = Array.isArray(value) ? value[0] ?? '' : value;
        return (
          <SwitchInput
            value={stringValue}
            toggleOptions={column.toggleOptions!}
            onChange={onChange}
          />
        );
      }
      case 'multi-select': {
        const arrayValue = Array.isArray(value) ? value : [];
        return (
          <MultiSelectInput
            value={arrayValue}
            options={options}
            onChange={onChange}
          />
        );
      }
      case 'select': {
        const stringValue = Array.isArray(value) ? value.join(',') : value;
        return (
          <SingleSelectInput
            value={stringValue}
            options={options}
            onChange={onChange}
          />
        );
      }
      case 'number': {
        const stringValue = Array.isArray(value) ? value.join(',') : value;
        return <NumberInput value={stringValue} onChange={onChange} />;
      }
      case 'date': {
        const stringValue = Array.isArray(value) ? value.join(',') : value;
        return <DateInput value={stringValue} onChange={onChange} />;
      }
      default: {
        const stringValue = Array.isArray(value) ? value.join(',') : value;
        return <TextInput value={stringValue} onChange={onChange} />;
      }
    }
  };

  return (
    <Field label="Value" required={kind !== 'toggle'}>
      {renderInput()}
    </Field>
  );
};
